import type { AxiosInstance } from 'axios';
import moment from 'moment';
import type { AppDispatch } from '../redux/Store';
import { getEmployeeLocations, getLocationById } from '../api/locations';
import { getOwnerSummary } from '../api/backofficeSummary';
import {
  clearLocationError,
  setCurrentLocation,
  setCurrentLocationId,
  setLocationError,
  setLocationLoading,
  setLocations,
} from '../redux/locationSlice';
import {
  ensureCompanyProfileInitialized,
  setCompanyProfile,
} from '../redux/backoffice/companyProfileSlice';
import type { CompanyProfileData } from '../redux/backoffice/companyProfileSlice';
import {
  setSummaryData,
  toSummaryData,
} from '../redux/backoffice/summarySlice';

export type PreloadEmployeeSessionArgs = {
  dispatch: AppDispatch;
  axiosPrivate: AxiosInstance;
  accessToken: string;
  employeeId: string;
};

export type PreloadEmployeeSessionResult = {
  currentLocationId: string | null;
};

/**
 * Employee-side preload right after login: hydrate location slice so
 * `currentLocation` / `currentLocationId` exist before any screen mounts.
 * Uses explicit ids/tokens (no stale hook closures).
 */
export async function preloadEmployeeSession(
  args: PreloadEmployeeSessionArgs,
): Promise<PreloadEmployeeSessionResult> {
  const { dispatch, axiosPrivate, accessToken, employeeId } = args;
  if (!accessToken || !employeeId) {
    return { currentLocationId: null };
  }

  try {
    dispatch(setLocationLoading(true));
    dispatch(clearLocationError());

    const response = await getEmployeeLocations(axiosPrivate, employeeId, {
      accessToken,
    });
    console.log('response', response);

    if (response.data.success || response.data.status === 200) {
      const locationsData = response.data.data.allowed_locations;
      const currentLocationData = response.data.data.current_location;
      dispatch(setLocations(locationsData));
      dispatch(setCurrentLocation(currentLocationData));
      const id = currentLocationData?._id
        ? String(currentLocationData._id)
        : null;
      dispatch(setCurrentLocationId(id));
      return { currentLocationId: id };
    }

    dispatch(
      setLocationError(response.data.message || 'Failed to fetch locations'),
    );
    return { currentLocationId: null };
  } catch (e: any) {
    console.error('Failed to fetch locations:', e);
    dispatch(setLocationError(e?.message || 'Failed to fetch locations'));
    return { currentLocationId: null };
  } finally {
    dispatch(setLocationLoading(false));
  }
}

/** Same mapping as `BackOfficeCompanyProfileScreen` `load()` → Redux. */
export function mapLocationToCompanyProfile(loc: any): CompanyProfileData {
  return {
    bp_location_owner_id: loc.bp_location_owner_id,
    bp_location_name: String(loc.bp_location_name || ''),
    bp_location_type: String(loc.bp_location_type || 'General Shop'),
    bp_location_currency: String(
      (loc?.bp_location_settings?.bp_location_currency as string) || 'KES',
    ),
    bp_location_is_default: Boolean(loc.bp_location_is_default),
    bp_business_description: String(loc.bp_location_description || ''),
    bp_location_address_information: {
      street: String(
        loc?.bp_location_address_information?.bp_location_street || '',
      ),
      city: String(
        loc?.bp_location_address_information?.bp_location_town || '',
      ),
      state: String(
        loc?.bp_location_address_information?.bp_location_state || '',
      ),
      postal_code: String(
        loc?.bp_location_address_information?.bp_location_postal_code || '',
      ),
      country: String(
        loc?.bp_location_address_information?.bp_location_country || '',
      ),
    },
    bp_location_contact_information: {
      phone: String(
        loc?.bp_location_contact_information?.bp_location_phone_number || '',
      ),
      email: String(
        loc?.bp_location_contact_information?.bp_location_email_address || '',
      ),
    },
  };
}

export type PreloadOwnerSessionArgs = {
  dispatch: AppDispatch;
  axiosBackoffice: AxiosInstance;
  ownerAccessToken: string;
  ownerId: string;
  currentLocationId: string | null;
};

/**
 * Owner-side preload after owner tokens exist: company profile slice and
 * Stats tab "Today" summary cache. Dashboard totals are left to Home /
 * Failures are isolated (login still succeeds).
 */
export async function preloadOwnerSession(
  args: PreloadOwnerSessionArgs,
): Promise<void> {
  const {
    dispatch,
    axiosBackoffice,
    ownerAccessToken,
    ownerId,
    currentLocationId,
  } = args;

  if (!ownerAccessToken || !ownerId) return;

  const auth = { accessToken: ownerAccessToken };
  const startDate = moment().startOf('day').toISOString();
  const endDate = moment().endOf('day').toISOString();

  const runCompanyProfile = async () => {
    if (!currentLocationId) return;
    dispatch(ensureCompanyProfileInitialized());
    const res = await getLocationById(axiosBackoffice, currentLocationId, auth);
    const loc = res?.data?.data;
    if (loc) {
      dispatch(setCompanyProfile(mapLocationToCompanyProfile(loc)));
    }
  };

  const runTodaySummary = async () => {
    const res = await getOwnerSummary(
      axiosBackoffice,
      ownerId,
      startDate,
      endDate,
      auth,
    );
    if (res?.status === 200) {
      const payload = (res?.data?.data ?? res?.data) || {};
      dispatch(
        setSummaryData({
          range: 'Today',
          data: toSummaryData(payload),
        }),
      );
    }
  };

  await Promise.allSettled([runCompanyProfile(), runTodaySummary()]);
}
